import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import type {
  EChartsOption,
  EChartsType,
  LegendComponentOption,
  SeriesOption,
} from "echarts";
import type {
  CallbackDataParams,
  TopLevelFormatterParams,
  TooltipFormatterCallback,
} from "echarts/types/dist/shared";
import type { LineChartItem } from "@/lib/utils/marketSimilarity/types";

type Props = {
  data: LineChartItem[];
  loading: boolean;
};

type LegendState = "normal" | "neither";
type LegendSelectionMap = Record<string, LegendState>;
type LegendSelectEventParams = {
  name: string;
  selected?: Record<string, boolean>;
};
type AxisAwareCallbackParams = CallbackDataParams & {
  axisValue?: string | number;
  axisValueLabel?: string;
};

type TimelinePoint = {
  dateString: string;
  value: number;
  colorName: string;
  colorValue: string;
};

const MUTED_LINE_COLOR = "#9ca3af";

// Memoized color name computation with caching
const colorNameCache = new Map<string, string>();

function getColorName(color: string): string {
  if (colorNameCache.has(color)) {
    return colorNameCache.get(color)!;
  }

  const colorLower = color.toLowerCase().trim();
  let result: string;

  // Check for named colors first
  if (colorLower.includes("blue")) result = "Avg Correl";
  else if (colorLower.includes("gray") || colorLower.includes("grey")) result = "Neither";
  else if (colorLower.includes("red")) result = "Euclidean";
  else if (colorLower.includes("green")) result = "Green";
  else if (colorLower.includes("purple") || colorLower.includes("violet")) result = "Both";
  else if (colorLower.includes("orange")) result = "Orange";
  else if (colorLower.includes("yellow")) result = "Yellow";
  else if (colorLower.includes("pink")) result = "Pink";
  else if (colorLower.includes("cyan")) result = "Cyan";
  else if (colorLower.includes("magenta")) result = "Magenta";
  else {  result = "Unknown"; }


  colorNameCache.set(color, result);
  return result;
}

function parseDateString(dateString: string): Date {
  const [month, day, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day);
}

export function LineChartPanel({ data, loading }: Props) {
  const [legendSelection, setLegendSelection] = useState<LegendSelectionMap>({});
  const chartRef = useRef<EChartsType | null>(null);
  const suppressLegendEventRef = useRef(false);

  // Memoize processed data separately from chart options
  const processedData = useMemo(() => {
    if (data.length === 0) {
      return null;
    }

    const dateEntryMap = new Map<
      string,
      {
        date: Date;
        value: number;
        colorName: string;
        colorValue: string;
        dateIndex: number;
      }
    >();
    const colorNameToColorValue = new Map<string, string>();

    for (const item of data) {
      const colorName = getColorName(item.color);
      const colorValue = item.color;

      if (!colorNameToColorValue.has(colorName)) {
        let mappedColor = colorValue;
        if (colorName === "Neither") {
          mappedColor = "#dcd2d2";
        }
        colorNameToColorValue.set(colorName, mappedColor);
      }

      for (const point of item.points) {
        const parsedDate = parseDateString(point.dateString);
        const existingEntry = dateEntryMap.get(point.dateString);

        if (!existingEntry || point.dateIndex >= existingEntry.dateIndex) {
          dateEntryMap.set(point.dateString, {
            date: parsedDate,
            value: point.adjustedClose,
            colorName,
            colorValue,
            dateIndex: point.dateIndex,
          });
        }
      }
    }

    const timelinePoints: TimelinePoint[] = Array.from(dateEntryMap.entries())
      .sort((a, b) => a[1].date.getTime() - b[1].date.getTime())
      .map(([dateString, info]) => ({
        dateString,
        value: info.value,
        colorName: info.colorName,
        colorValue: info.colorValue,
      }));

    const xAxisData = timelinePoints.map((point) => point.dateString);
    const dateStringToPosition = new Map<string, number>();
    timelinePoints.forEach((point, index) => {
      dateStringToPosition.set(point.dateString, index);
    });
    const legendNames = Array.from(colorNameToColorValue.keys());
    const neitherColor = colorNameToColorValue.get("Neither") ?? MUTED_LINE_COLOR;

    return {
      timelinePoints,
      xAxisData,
      legendNames,
      colorNameToColorValue,
      dateStringToPosition,
      neitherColor,
    };
  }, [data]);

  const { option, legendNames } = useMemo<{
    option: EChartsOption | null;
    legendNames: string[];
  }>(() => {
    if (!processedData) {
      return { option: null, legendNames: [] };
    }

    const {
      timelinePoints,
      xAxisData,
      legendNames: availableLegendNames,
      colorNameToColorValue,
      dateStringToPosition,
      neitherColor,
    } = processedData;

    // Build legend data
    const legendData: NonNullable<LegendComponentOption["data"]> = availableLegendNames.map((colorName) => {
      const baseColor = colorNameToColorValue.get(colorName) ?? MUTED_LINE_COLOR;
      const state = legendSelection[colorName] ?? "normal";
      const isNeitherState = state === "neither";
      const legendFontWeight: "normal" | "bold" = "normal";

      return {
        name: colorName,
        icon: "line",
        selected: true, // Always keep series visible
        textStyle: {
          color: isNeitherState ? neitherColor : baseColor,
          opacity: 1,
          fontWeight: legendFontWeight,
        },
      };
    });

    const dataLength = timelinePoints.length;
    const seriesDataByColor = new Map<string, (number | null)[]>();
    availableLegendNames.forEach((colorName) => {
      seriesDataByColor.set(colorName, new Array(dataLength).fill(null));
    });

    timelinePoints.forEach((point) => {
      const position = dateStringToPosition.get(point.dateString);
      if (position == null) {
        return;
      }
      const seriesData = seriesDataByColor.get(point.colorName);
      if (seriesData) {
        seriesData[position] = point.value;
      }
    });

    const series: SeriesOption[] = availableLegendNames.map((colorName, index) => {
      const baseColor = colorNameToColorValue.get(colorName) ?? MUTED_LINE_COLOR;
      const state = legendSelection[colorName] ?? "normal";
      const isNeitherState = state === "neither";
      const resolvedColor = isNeitherState ? neitherColor : baseColor;
      const resolvedOpacity = 1;
      const seriesData = seriesDataByColor.get(colorName) ?? [];

      return {
        name: colorName,
        type: "line" as const,
        data: seriesData,
        lineStyle: {
          color: resolvedColor,
          opacity: resolvedOpacity,
        },
        itemStyle: {
          color: resolvedColor,
          opacity: resolvedOpacity,
        },
        connectNulls: false,
        symbol: "circle",
        symbolSize: 4,
        legendHoverLink: true,
        z: index
      };
    });

    const tooltipFormatter: TooltipFormatterCallback<TopLevelFormatterParams> = (rawParams) => {
      const paramsArray: CallbackDataParams[] = Array.isArray(rawParams)
        ? (rawParams as CallbackDataParams[])
        : [rawParams as CallbackDataParams];

      const targetParam = paramsArray.find(
        (p) =>
          typeof p.value === "number" ||
          (Array.isArray(p.value) && typeof p.value[1] === "number")
      );

      if (!targetParam) {
        return "";
      }

      const numericValue =
        typeof targetParam.value === "number"
          ? targetParam.value
          : Array.isArray(targetParam.value) && typeof targetParam.value[1] === "number"
            ? targetParam.value[1]
            : null;

      const axisParam = targetParam as AxisAwareCallbackParams;
      const axisLabel =
        axisParam.axisValueLabel ??
        (typeof axisParam.axisValue === "string" ? axisParam.axisValue : axisParam.name ?? "");

      if (numericValue == null) {
        return axisLabel ?? "";
      }

      return `${axisLabel}<br/>${numericValue.toFixed(2)}`;
    };

    return {
      option: {
        title: {
          text: "",
          left: "center",
        },
        tooltip: {
          trigger: "axis",
          formatter: tooltipFormatter,
        },
        legend: {
          data: legendData,
          top: 39,
          selectedMode: true,
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "15%",
          containLabel: true,
        },
        dataZoom: [
          {
            type: "inside",
            xAxisIndex: 0,
            start: 0,
            end: 100,
          },
          {
            type: "slider",
            xAxisIndex: 0,
            start: 0,
            end: 100,
            bottom: "3%",
            height: 20,
          },
        ],
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: xAxisData,
          name: "",
          nameLocation: "middle",
          nameGap: 30,
        },
        yAxis: {
          type: "value",
          name: "",
          nameLocation: "middle",
          nameGap: 50,
        },
        series: series,
      },
      legendNames: availableLegendNames,
    };
  }, [processedData, legendSelection]);

  useEffect(() => {
    if (legendNames.length === 0) {
      setLegendSelection((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }

    setLegendSelection((prev) => {
      let changed = false;
      const nextState: LegendSelectionMap = {};

      legendNames.forEach((name) => {
        if (prev[name] === undefined) {
          changed = true;
        }
        nextState[name] = prev[name] ?? "normal";
      });

      if (Object.keys(prev).length !== legendNames.length) {
        changed = true;
      }

      return changed ? nextState : prev;
    });
  }, [legendNames]);

  const handleLegendSelectChanged = useCallback(
    (params: LegendSelectEventParams, chartInstance?: EChartsType) => {
      if (suppressLegendEventRef.current) {
        suppressLegendEventRef.current = false;
        return;
      }

      const legendName = params.name;

      // Block clicks on the actual "Neither" legend entry - immediately re-select it
      if (legendName === "Neither") {
        const instance = chartRef.current ?? chartInstance ?? null;
        if (instance != null) {
          suppressLegendEventRef.current = true;
          // Force re-select in next tick to ensure it happens after ECharts processes the click
          setTimeout(() => {
            instance.dispatchAction({
              type: "legendSelect",
              name: legendName,
              selected: true,
            });
            suppressLegendEventRef.current = false;
          }, 0);
        }
        return;
      }

      // Toggle between "normal" and "neither" states for other legend entries
      setLegendSelection((prev) => {
        const previousState = prev[legendName] ?? "normal";
        const nextState: LegendState = previousState === "normal" ? "neither" : "normal";
        return {
          ...prev,
          [legendName]: nextState,
        };
      });

      // Always re-select to prevent hiding - do this after state update
      const instance = chartRef.current ?? chartInstance ?? null;
      if (instance != null) {
        suppressLegendEventRef.current = true;
        // Use setTimeout to ensure re-select happens after ECharts processes the click
        setTimeout(() => {
          instance.dispatchAction({
            type: "legendSelect",
            name: legendName,
            selected: true,
          });
          suppressLegendEventRef.current = false;
        }, 0);
      }
    },
    []
  );

  const chartEvents = useMemo(
    () => ({
      legendselectchanged: handleLegendSelectChanged,
    }),
    [handleLegendSelectChanged]
  );

  const handleChartReady = useCallback((instance: EChartsType) => {
    chartRef.current = instance;
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-gray-600">
          <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <span>Loading chart data…</span>
        </div>
      </div>
    );
  }

  if (data.length === 0 || option === null) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600">
        <div className="text-center">
          <p className="mb-2">No chart data available.</p>
          <p className="text-sm">Adjust inputs and click &quot;Fetch Similarity Data&quot;.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <ReactECharts
        option={option}
        style={{ height: "500px", width: "100%" }}
        opts={{ renderer: "canvas" }}
        onEvents={chartEvents}
        onChartReady={handleChartReady}
      />
    </div>
  );
}

